const BASE = 'https://onefinallegacy-programme-api.1fl.workers.dev';
const CLIENT_ID = '186691154508-jubh5vb48pujd0ali5lpt22cpg6483r8.apps.googleusercontent.com';
let token = '';
let expiresAt = 0;

export const programmeClientId = () => CLIENT_ID;

export function setProgrammeToken(value) {
  token = value || '';
  expiresAt = 0;
  if (!token) return;
  try {
    const segment = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    expiresAt = Number(JSON.parse(atob(segment)).exp) * 1000;
  } catch {
    token = '';
  }
}

export function hasProgrammeToken() {
  if (token && Date.now() < expiresAt - 30000) return true;
  token = '';
  return false;
}

export class ProgrammeApiError extends Error {
  constructor(message, status, code = '', reason = '') {
    super(message);
    this.status = status;
    this.code = code;
    this.reason = reason;
  }
}

const messages = {
  401: 'Your programme session has expired. Please sign in again.',
  403: 'You do not have permission to do that.',
  404: 'This content is not available.',
  429: 'Too many requests. Please wait and try again.',
};

export async function programmeRequest(path, options = {}, publicRequest = false) {
  if (!publicRequest && !hasProgrammeToken()) throw new ProgrammeApiError(messages[401], 401);
  const headers = { ...options.headers };
  if (!publicRequest) headers.Authorization = `Bearer ${token}`;
  if (options.body && !(options.body instanceof Blob) && typeof options.body !== 'string') {
    headers['Content-Type'] = 'application/json';
    options = { ...options, body: JSON.stringify(options.body) };
  }
  let response;
  try {
    response = await fetch(`${BASE}${path}`, { ...options, headers });
  } catch (error) {
    throw new ProgrammeApiError(error?.message || 'Could not reach the programme service. Please try again.', 0);
  }
  if (response.status === 401) setProgrammeToken('');
  if (response.status === 204) return null;
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const message = typeof body?.error === 'string'
      ? body.error
      : messages[response.status] || 'The request could not be completed.';
    throw new ProgrammeApiError(message, response.status, body?.error || '', body?.reason || '');
  }
  return body;
}

export const rowsOf = value => Array.isArray(value)
  ? value
  : value?.rows ?? value?.programmes ?? value?.items ?? value?.pieces ?? value?.staff ?? [];

export const programmeById = id => programmeRequest(`/api/programmes/${encodeURIComponent(id)}`);
export const programmeList = () => programmeRequest('/api/programmes');
export const createProgramme = body => programmeRequest('/api/programmes', { method: 'POST', body });
export const patchProgramme = (id, body) => programmeRequest(`/api/programmes/${encodeURIComponent(id)}`, { method: 'PATCH', body });
export const deleteProgramme = id => programmeRequest(`/api/programmes/${encodeURIComponent(id)}`, { method: 'DELETE' });
export const programmeStaff = id => programmeRequest(`/api/programmes/${encodeURIComponent(id)}/staff`);
export const addProgrammeStaff = (id, body) => programmeRequest(`/api/programmes/${encodeURIComponent(id)}/staff`, { method: 'POST', body });
export const removeProgrammeStaff = (id, email) => programmeRequest(`/api/programmes/${encodeURIComponent(id)}/staff/${encodeURIComponent(email)}`, { method: 'DELETE' });
export const library = () => programmeRequest('/api/library');
export const libraryPiece = id => programmeRequest(`/api/library/${encodeURIComponent(id)}`);
export const libraryPreview = (id, instrument) => programmeRequest(`/api/library/${encodeURIComponent(id)}/preview?instrument=${encodeURIComponent(instrument)}`);
export const publicProgramme = slug => programmeRequest(`/api/public/${encodeURIComponent(slug)}`, {}, true);
export const itemAudio = (id, itemId) => programmeRequest(`/api/programmes/${encodeURIComponent(id)}/items/${encodeURIComponent(itemId)}/audio`);

export const createProgrammeItem = (id, body) => programmeRequest(`/api/programmes/${encodeURIComponent(id)}/items`, { method: 'POST', body });
export const patchProgrammeItem = (id, itemId, body) => programmeRequest(`/api/programmes/${encodeURIComponent(id)}/items/${encodeURIComponent(itemId)}`, { method: 'PATCH', body });
export const deleteProgrammeItem = (id, itemId) => programmeRequest(`/api/programmes/${encodeURIComponent(id)}/items/${encodeURIComponent(itemId)}`, { method: 'DELETE' });

export async function uploadProgrammeCover(id, file) {
  if (!(file instanceof Blob)) throw new ProgrammeApiError('Choose an image file to upload.', 400);
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
    throw new ProgrammeApiError('Cover images must be JPEG, PNG, or WebP.', 400);
  }
  if (file.size > 5 * 1024 * 1024) {
    throw new ProgrammeApiError('Cover images must be 5 MB or smaller.', 413);
  }
  return programmeRequest(`/api/programmes/${encodeURIComponent(id)}/cover`, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
}

export const parseJson = (value, fallback) => {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value !== 'string') return value;
  try { return JSON.parse(value); } catch { return fallback; }
};
const arr = value => (Array.isArray(value) ? value : []);

// Legacy table-shaped calls are intentionally refused rather than silently
// routing programme data through NCB or guessing an API endpoint.
export async function readTable() {
  throw new ProgrammeApiError('This programme screen must be migrated to the programme API.', 403);
}

export async function readOne() {
  throw new ProgrammeApiError('This programme screen must be migrated to the programme API.', 403);
}

export async function createRow() {
  return { ok: false, error: 'This programme screen must be migrated to the programme API.' };
}

export const updateRow = async () => ({
  status: 'error',
  error: 'This programme screen must be migrated to the programme API.',
});

export const deleteRow = async () => ({
  status: 'error',
  error: 'This programme screen must be migrated to the programme API.',
});

export const normPiece = row => ({
  rowId: row?.id,
  id: row?.piece_id ?? row?.id,
  title: row?.title || '',
  category: row?.category || '',
  composer: row?.composer || '',
  tune: row?.tune || '',
  year: row?.year || '',
  mood: row?.mood || '',
  length: row?.length || '',
  instruments: arr(parseJson(row?.instruments, [])),
  files: arr(parseJson(row?.files, [])),
  playlists: arr(parseJson(row?.playlists, [])),
  credit: row?.credit || '',
  rights: parseJson(row?.rights, {}) || {},
  lyricsVerdict: row?.lyricsVerdict || row?.lyrics_verdict || '',
  lyricsReviewed: row?.lyricsReviewed ?? row?.lyrics_reviewed ?? false,
});

export const normRights = row => ({
  pieceId: row?.piece_id,
  verdict: row?.verdict,
  lyricist: row?.lyricist,
  textYear: row?.text_year,
  NG: row?.ng, UK: row?.uk, EU: row?.eu, US: row?.us,
  holdUntilNgUkEu: row?.hold_until_ng_uk_eu,
  holdUntilUs: row?.hold_until_us,
  servedIn: arr(parseJson(row?.served_in, [])),
  condition: row?.condition,
  note: row?.note,
});

export const normLyrics = row => ({
  pieceId: row?.piece_id,
  verses: arr(parseJson(row?.verses, [])),
  sourceEdition: row?.source_edition || '',
  status: row?.status,
});

export const normProgramme = row => ({
  ...row,
  id: row?.id,
  eventId: row?.eventId ?? row?.event_id ?? '',
  ownerUserId: row?.ownerUserId ?? row?.owner_user_id ?? '',
  title: row?.title || '',
  subtitle: row?.subtitle || '',
  deceasedName: row?.deceasedName ?? row?.deceased_name ?? '',
  dates: row?.dates || '',
  coverImage: row?.coverUrl ?? row?.coverImage ?? row?.cover_image ?? '',
  details: row?.details || '',
  status: row?.status || 'draft',
  audioMode: row?.audioMode ?? row?.audio_mode ?? 'off',
  shareSlug: row?.shareSlug ?? row?.share_slug ?? '',
  role: row?.role || '',
  items: arr(row?.items),
  credits: arr(row?.credits),
});

export const programmeToRow = programme => {
  const map = {
    title: 'title', subtitle: 'subtitle', deceasedName: 'deceasedName', dates: 'dates',
    coverImage: 'coverImage', details: 'details', status: 'status', audioMode: 'audioMode',
  };
  const output = {};
  Object.keys(programme || {}).forEach(key => { if (map[key]) output[map[key]] = programme[key]; });
  return output;
};

export const programmeBody = programme => {
  const allowed = ['title', 'subtitle', 'deceasedName', 'dates', 'details', 'eventId', 'audioMode', 'status'];
  return Object.fromEntries(allowed
    .filter(key => Object.prototype.hasOwnProperty.call(programme || {}, key))
    .map(key => [key, programme[key]]));
};

export const normItem = row => ({
  ...row,
  id: row?.id,
  order: Number(row?.sortOrder ?? row?.order ?? row?.sort_order) || 0,
  type: row?.type || 'custom',
  title: row?.title || '',
  text: row?.text || '',
  textLabel: row?.textLabel || '',
  pieceId: row?.piece?.id ?? row?.pieceId ?? row?.piece_id ?? '',
  piece: row?.piece || null,
  versesChosen: arr(parseJson(row?.versesChosen ?? row?.verses_chosen, [])),
  instrument: row?.instrument || 'Piano',
  hostOwnText: row?.hostOwnText === true || Number(row?.host_own_text) === 1,
  hostTextConfirmed: row?.hostTextConfirmed === true,
  hostTextConfirmedAt: row?.hostTextConfirmedAt || '',
  lyrics: row?.lyrics,
  notes: row?.notes || '',
  audioUrl: row?.audioUrl ?? row?.audio_url ?? '',
  audioSource: row?.audioSource ?? row?.audio_source ?? '',
});

export const itemToRow = programmeItem => {
  const output = {};
  const copy = { order: 'order', type: 'type', title: 'title', text: 'text', pieceId: 'pieceId', instrument: 'instrument', notes: 'notes', audioUrl: 'audioUrl', audioSource: 'audioSource' };
  Object.keys(programmeItem || {}).forEach(key => { if (copy[key]) output[copy[key]] = programmeItem[key]; });
  if ('versesChosen' in programmeItem) output.versesChosen = programmeItem.versesChosen;
  if ('hostOwnText' in programmeItem) output.hostOwnText = programmeItem.hostOwnText;
  if ('hostTextConfirmed' in programmeItem) output.hostTextConfirmed = programmeItem.hostTextConfirmed;
  return output;
};

export const programmeItemBody = programmeItem => {
  const allowed = ['type', 'title', 'text', 'pieceId', 'versesChosen', 'instrument', 'hostOwnText', 'hostTextConfirmed', 'notes'];
  const output = {};
  allowed.forEach(key => {
    if (Object.prototype.hasOwnProperty.call(programmeItem || {}, key)) output[key] = programmeItem[key];
  });
  if (Object.prototype.hasOwnProperty.call(programmeItem || {}, 'sortOrder')) output.sortOrder = programmeItem.sortOrder;
  else if (Object.prototype.hasOwnProperty.call(programmeItem || {}, 'order')) output.sortOrder = programmeItem.order;
  return output;
};

export async function loadLibraryData() {
  const result = await library();
  return { library: rowsOf(result).map(normPiece), rightsMap: {}, lyricsMap: {} };
}